import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { DashboardLayout } from '@/components/DashboardLayout';
import { 
  Search, Plus, Minus, Trash2, ShoppingBag, CreditCard, 
  Banknote, QrCode, Receipt, X, User, Phone, MapPin, Printer, Truck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import OrderPrintView from '@/components/OrderPrintView';

const PAYMENT_METHODS = [
  { id: 'cash', name: 'Dinheiro', icon: Banknote, color: 'text-green-400' },
  { id: 'pix', name: 'PIX', icon: QrCode, color: 'text-cyan-400' },
  { id: 'card', name: 'Cartão', icon: CreditCard, color: 'text-blue-400' },
];

const OnlineSales = () => {
  const [products, setProducts] = useState([]);
  const [store, setStore] = useState(null);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [cart, setCart] = useState([]);
  const [discount, setDiscount] = useState(0);
  
  // Customer info
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  
  // Address
  const [deliveryType, setDeliveryType] = useState('delivery');
  const [cep, setCep] = useState('');
  const [address, setAddress] = useState({
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: ''
  });
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [loadingCep, setLoadingCep] = useState(false);
  const [calcFee, setCalcFee] = useState(false);
  const [feeMessage, setFeeMessage] = useState('');
  
  // Payment
  const [paymentMethod, setPaymentMethod] = useState('pix');
  const [notes, setNotes] = useState('');
  
  // Modals
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [lastOrder, setLastOrder] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    fetchData();
    searchRef.current?.focus();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = products.filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.sku?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProducts(filtered);
    } else {
      setFilteredProducts([]);
    }
  }, [searchTerm, products]);

  const fetchData = async () => {
    try {
      const [prodRes, storeRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/stores/me`)
      ]);
      setProducts(prodRes.data.filter(p => p.is_active !== false));
      setStore(storeRes.data);
    } catch (e) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const searchCep = async () => {
    if (cep.length < 8) return;
    setLoadingCep(true);
    try {
      const cleanCep = cep.replace(/\D/g, '');
      const res = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setAddress(prev => ({
          ...prev,
          street: data.logradouro || prev.street,
          neighborhood: data.bairro || prev.neighborhood,
          city: data.localidade || prev.city,
          state: data.uf || prev.state
        }));
      } else {
        toast.error('CEP não encontrado');
      }
    } catch (e) {
      toast.error('Erro ao buscar CEP');
    } finally {
      setLoadingCep(false);
    }
  };

  const calculateDeliveryFee = async () => {
    if (!store?.slug) return;
    if (!address.street || !address.number) {
      toast.error('Preencha rua e número para calcular a taxa');
      return;
    }
    setCalcFee(true);
    try {
      const res = await axios.post(`${API}/public/stores/${store.slug}/calculate-delivery`, {
        street: address.street, number: address.number, neighborhood: address.neighborhood,
        city: address.city || store.address?.city || '', state: address.state || store.address?.state || '',
        zipcode: cep
      });
      setDeliveryFee(Number(res.data.fee) || 0);
      const dist = res.data.distance_km ? ` (${Number(res.data.distance_km).toFixed(1)} km)` : '';
      setFeeMessage(res.data.available === false ? (res.data.message || 'Fora da área de entrega') : `Taxa calculada${dist}`);
    } catch (e) {
      toast.error('Erro ao calcular taxa de entrega');
    } finally {
      setCalcFee(false);
    }
  };

  const addToCart = (product) => {
    const existing = cart.find(item => item.product_id === product.id);
    if (existing) {
      setCart(cart.map(item => 
        item.product_id === product.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, {
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        unit_price: product.promo_price || product.price,
        selected_variants: [],
        selected_addons: []
      }]);
    }
    setSearchTerm('');
    toast.success(`${product.name} adicionado`);
  };

  const updateQuantity = (productId, delta) => {
    setCart(cart.map(item => {
      if (item.product_id === productId) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? { ...item, quantity: newQty } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  const subtotal = cart.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  const total = subtotal - discount + (deliveryType === 'delivery' ? deliveryFee : 0);

  const clearForm = () => {
    setCart([]);
    setDiscount(0);
    setCustomerName('');
    setCustomerPhone('');
    setCep('');
    setAddress({ street: '', number: '', complement: '', neighborhood: '', city: '', state: '' });
    setDeliveryFee(0);
    setFeeMessage('');
    setPaymentMethod('pix');
    setNotes('');
    setDeliveryType('delivery');
  };

  const handleCreateOrder = async () => {
    if (cart.length === 0) {
      toast.error('Adicione produtos ao carrinho');
      return;
    }
    if (!customerName || !customerPhone) {
      toast.error('Preencha nome e telefone do cliente');
      return;
    }
    if (deliveryType === 'delivery' && (!address.street || !address.number)) {
      toast.error('Preencha o endereço de entrega');
      return;
    }
    
    setProcessing(true);
    try {
      const orderData = {
        customer_name: customerName,
        customer_phone: customerPhone,
        delivery_type: deliveryType,
        address: deliveryType === 'delivery' ? {
          zipcode: cep,
          street: address.street,
          number: address.number,
          complement: address.complement,
          neighborhood: address.neighborhood,
          city: address.city || '',
          state: address.state || ''
        } : null,
        items: cart,
        payment_method: paymentMethod,
        notes: notes || 'Pedido criado via Venda Direta Online',
        delivery_fee: deliveryType === 'delivery' ? deliveryFee : 0,
        discount,
        lgpd_consent: true
      };
      
      const res = await axios.post(`${API}/orders/manual`, orderData);
      setLastOrder(res.data);
      setShowConfirmModal(false);
      setShowSuccessModal(true);
      clearForm();
      toast.success('Pedido criado com sucesso!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao criar pedido');
    } finally {
      setProcessing(false);
    }
  };

  const handlePrint = () => {
    setShowSuccessModal(false);
    setShowPrintModal(true);
  };

  if (loading) {
    return (
      <DashboardLayout title="Venda Direta Online">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-[#ff6b00]"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Venda Direta Online">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[calc(100vh-140px)] overflow-hidden">
        {/* Products & Cart */}
        <div className="lg:col-span-2 flex flex-col min-h-0">
          {/* Search */}
          <Card className="card-adegga flex-shrink-0">
            <CardContent className="p-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  ref={searchRef}
                  placeholder="Buscar produto..."
                  data-testid="online-sales-search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-[#0f0f15] border-white/10 text-white text-lg h-12"
                />
              </div>
              
              {/* Search Results */}
              {filteredProducts.length > 0 && (
                <div className="mt-3 max-h-48 overflow-y-auto space-y-2">
                  {filteredProducts.slice(0, 8).map(product => (
                    <button
                      key={product.id}
                      onClick={() => addToCart(product)}
                      data-testid={`online-sales-result-${product.id}`}
                      className="w-full flex items-center justify-between p-2 rounded-lg bg-[#0f0f15] hover:bg-[#1a1a2e] transition-colors"
                    >
                      <span className="text-white">{product.name}</span>
                      <span className="text-[#ff6b00] font-bold">
                        R$ {(product.promo_price || product.price).toFixed(2)}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Cart */}
          <Card className="card-adegga flex-1 overflow-hidden mt-3 min-h-0">
            <CardHeader className="pb-2 flex-shrink-0">
              <CardTitle className="text-white flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-[#ff6b00]" />
                Itens do Pedido
                {cart.length > 0 && <Badge className="bg-[#ff6b00] ml-2">{cart.length}</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0 overflow-y-auto" style={{ maxHeight: 'calc(100% - 50px)' }}>
              {cart.length === 0 ? (
                <div className="text-center py-8 text-white/40">
                  <ShoppingBag className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Busque e adicione produtos</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {cart.map(item => (
                    <div key={item.product_id} className="flex items-center gap-3 p-2 bg-[#0f0f15] rounded-lg">
                      <div className="flex-1">
                        <p className="text-white text-sm font-medium">{item.product_name}</p>
                        <p className="text-[#ff6b00] text-sm">R$ {item.unit_price.toFixed(2)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="ghost" onClick={() => updateQuantity(item.product_id, -1)} className="h-7 w-7 p-0">
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className="text-white w-6 text-center">{item.quantity}</span>
                        <Button size="sm" variant="ghost" onClick={() => updateQuantity(item.product_id, 1)} className="h-7 w-7 p-0">
                          <Plus className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => removeFromCart(item.product_id)} className="h-7 w-7 p-0 text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Customer & Payment Panel */}
        <div className="flex flex-col min-h-0 overflow-y-auto space-y-3">
          {/* Customer Info */}
          <Card className="card-adegga flex-shrink-0">
            <CardHeader className="pb-2 pt-3">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <User className="w-4 h-4 text-[#ff6b00]" />
                Dados do Cliente
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0 space-y-2">
              <Input
                placeholder="Nome do cliente"
                data-testid="online-sales-customer-name"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className="bg-[#0f0f15] border-white/10 text-white h-9"
              />
              <Input
                placeholder="Telefone (WhatsApp)"
                data-testid="online-sales-customer-phone"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                className="bg-[#0f0f15] border-white/10 text-white h-9"
              />
            </CardContent>
          </Card>

          {/* Delivery Type */}
          <Card className="card-adegga flex-shrink-0">
            <CardHeader className="pb-2 pt-3">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <Truck className="w-4 h-4 text-[#ff6b00]" />
                Tipo de Entrega
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0">
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setDeliveryType('delivery')}
                  data-testid="online-sales-type-delivery"
                  className={`p-2 rounded-lg border transition-all ${
                    deliveryType === 'delivery'
                      ? 'bg-[#ff6b00]/20 border-[#ff6b00] text-white'
                      : 'bg-[#0f0f15] border-white/10 text-white/60'
                  }`}
                >
                  <Truck className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-xs">Delivery</span>
                </button>
                <button
                  onClick={() => setDeliveryType('pickup')}
                  data-testid="online-sales-type-pickup"
                  className={`p-2 rounded-lg border transition-all ${
                    deliveryType === 'pickup'
                      ? 'bg-[#ff6b00]/20 border-[#ff6b00] text-white'
                      : 'bg-[#0f0f15] border-white/10 text-white/60'
                  }`}
                >
                  <ShoppingBag className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-xs">Retirada</span>
                </button>
              </div>
              
              {/* Address Fields */}
              {deliveryType === 'delivery' && (
                <div className="mt-3 space-y-2">
                  <div className="flex gap-2">
                    <Input
                      placeholder="CEP"
                      data-testid="online-sales-cep"
                      value={cep}
                      onChange={(e) => setCep(e.target.value)}
                      onBlur={searchCep}
                      className="bg-[#0f0f15] border-white/10 text-white h-9 flex-1"
                    />
                    <Button size="sm" onClick={searchCep} disabled={loadingCep} className="h-9">
                      {loadingCep ? '...' : 'Buscar'}
                    </Button>
                  </div>
                  <Input
                    placeholder="Rua"
                    data-testid="online-sales-street"
                    value={address.street}
                    onChange={(e) => setAddress({...address, street: e.target.value})}
                    className="bg-[#0f0f15] border-white/10 text-white h-9"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Número"
                      data-testid="online-sales-number"
                      value={address.number}
                      onChange={(e) => setAddress({...address, number: e.target.value})}
                      className="bg-[#0f0f15] border-white/10 text-white h-9"
                    />
                    <Input
                      placeholder="Complemento"
                      value={address.complement}
                      onChange={(e) => setAddress({...address, complement: e.target.value})}
                      className="bg-[#0f0f15] border-white/10 text-white h-9"
                    />
                  </div>
                  <Input
                    placeholder="Bairro"
                    data-testid="online-sales-neighborhood"
                    value={address.neighborhood}
                    onChange={(e) => setAddress({...address, neighborhood: e.target.value})}
                    className="bg-[#0f0f15] border-white/10 text-white h-9"
                  />
                  <Input
                    placeholder="Cidade"
                    value={address.city}
                    onChange={(e) => setAddress({...address, city: e.target.value})}
                    className="bg-[#0f0f15] border-white/10 text-white h-9"
                    data-testid="online-sales-city"
                  />
                  <div className="p-2 bg-[#ff6b00]/10 border border-[#ff6b00]/20 rounded-lg space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[#ff6b00] text-sm flex-1">Taxa de entrega (R$)</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.5"
                        value={deliveryFee}
                        onChange={(e) => setDeliveryFee(parseFloat(e.target.value) || 0)}
                        className="w-24 bg-[#0f0f15] border-white/10 text-white text-right h-8 text-sm"
                        data-testid="online-sales-delivery-fee"
                      />
                    </div>
                    <Button size="sm" onClick={calculateDeliveryFee} disabled={calcFee} className="w-full h-8 bg-[#ff6b00] hover:bg-[#ff8533]" data-testid="online-sales-calc-fee">
                      <MapPin className="w-4 h-4 mr-1" />
                      {calcFee ? 'Calculando...' : 'Calcular taxa pelo endereço'}
                    </Button>
                    {feeMessage && <p className="text-xs text-white/60" data-testid="online-sales-fee-message">{feeMessage}</p>}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment */}
          <Card className="card-adegga flex-shrink-0">
            <CardHeader className="pb-2 pt-3">
              <CardTitle className="text-white text-sm">Pagamento</CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0">
              <div className="grid grid-cols-2 gap-2">
                {PAYMENT_METHODS.map(method => (
                  <button
                    key={method.id}
                    onClick={() => setPaymentMethod(method.id)}
                    data-testid={`online-sales-pay-${method.id}`}
                    className={`p-2 rounded-lg border transition-all flex flex-col items-center gap-1 ${
                      paymentMethod === method.id
                        ? 'bg-[#ff6b00]/20 border-[#ff6b00] text-white'
                        : 'bg-[#0f0f15] border-white/10 text-white/60'
                    }`}
                  >
                    <method.icon className={`w-5 h-5 ${paymentMethod === method.id ? 'text-[#ff6b00]' : method.color}`} />
                    <span className="text-xs">{method.name}</span>
                  </button>
                ))}
              </div>
              
              <Textarea
                placeholder="Observações do pedido..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-3 bg-[#0f0f15] border-white/10 text-white min-h-[60px]"
              />
            </CardContent>
          </Card>

          {/* Totals */}
          <Card className="card-adegga flex-shrink-0">
            <CardContent className="p-3 space-y-2">
              <div className="flex justify-between text-sm text-white/60">
                <span>Subtotal</span>
                <span>R$ {subtotal.toFixed(2)}</span>
              </div>
              {deliveryType === 'delivery' && deliveryFee > 0 && (
                <div className="flex justify-between text-sm text-white/60">
                  <span>Taxa de entrega</span>
                  <span>R$ {deliveryFee.toFixed(2)}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="text-sm text-white/60">Desconto</span>
                <Input
                  type="number"
                  min="0"
                  value={discount || ''}
                  onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                  className="w-24 bg-[#0f0f15] border-white/10 text-white text-right h-8 text-sm"
                />
              </div>
              <div className="pt-2 border-t border-white/10">
                <div className="flex justify-between items-center">
                  <span className="text-lg text-white font-medium">Total</span>
                  <span className="text-2xl text-[#ff6b00] font-bold" data-testid="online-sales-total">R$ {total.toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="space-y-2 flex-shrink-0">
            <Button
              onClick={() => setShowConfirmModal(true)}
              data-testid="online-sales-create-btn"
              disabled={cart.length === 0 || !customerName || !customerPhone}
              className="w-full h-12 text-base bg-gradient-to-r from-[#ff6b00] to-[#ff8533] hover:from-[#ff8533] hover:to-[#ff9955]"
            >
              <Receipt className="w-5 h-5 mr-2" />
              Criar Pedido
            </Button>
            {cart.length > 0 && (
              <Button onClick={clearForm} variant="ghost" className="w-full h-9 text-white/60 hover:text-red-400">
                <X className="w-4 h-4 mr-2" />
                Limpar
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Confirm Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="bg-[#13131f] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Confirmar Pedido</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div className="p-3 bg-[#0f0f15] rounded-lg">
              <p className="text-white font-medium">{customerName}</p>
              <p className="text-white/60 text-sm">{customerPhone}</p>
              {deliveryType === 'delivery' && address.street && (
                <p className="text-white/60 text-sm mt-1">
                  {address.street}, {address.number} - {address.neighborhood}
                </p>
              )}
            </div>
            <div className="space-y-1">
              {cart.map(item => (
                <div key={item.product_id} className="flex justify-between text-sm">
                  <span className="text-white/80">{item.quantity}x {item.product_name}</span>
                  <span className="text-white">R$ {(item.quantity * item.unit_price).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="pt-2 border-t border-white/10">
              <div className="flex justify-between">
                <span className="text-white">Total</span>
                <span className="text-[#ff6b00] font-bold">R$ {total.toFixed(2)}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowConfirmModal(false)}>Cancelar</Button>
            <Button onClick={handleCreateOrder} disabled={processing} data-testid="online-sales-confirm-btn" className="bg-[#ff6b00] hover:bg-[#ff8533]">
              {processing ? 'Criando...' : 'Confirmar Pedido'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="bg-[#13131f] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-green-400 flex items-center gap-2">
              Pedido Criado com Sucesso!
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-white/80">
              Pedido <strong className="text-[#ff6b00]" data-testid="online-sales-order-number">#{lastOrder?.order_number}</strong> foi criado e está aguardando confirmação.
            </p>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="ghost" onClick={() => setShowSuccessModal(false)}>Fechar</Button>
            <Button onClick={handlePrint} data-testid="online-sales-print-btn" className="bg-[#ff6b00] hover:bg-[#ff8533]">
              <Printer className="w-4 h-4 mr-2" />
              Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showPrintModal && lastOrder && (
        <OrderPrintView order={lastOrder} store={store} onClose={() => setShowPrintModal(false)} />
      )}
    </DashboardLayout>
  );
};

export default OnlineSales;
